/**
 * B1.11 BusinessDashboard — mock↔live API dispatcher.
 *
 * Runtime switch driven by {@link APP_ENV.bizApiMode}:
 *   - `mock` (default): serves deterministic seed rows from
 *     {@link ./dashboard-api-mock} (Lovable / Storybook / pre-wire sessions).
 *   - `live`: routes through {@link ./dashboard-api-http} → `bizClient` →
 *     `/api/biz/dashboard/*` on the backend (Phase E1).
 *
 * Parallels the `cashier-api.ts`, `invoice-api.ts`, and `day-close-api.ts`
 * dispatchers shipped in Phase C2 / D / E1 FE rewires.
 *
 * @see dashboard-api-mock.ts
 * @see dashboard-api-http.ts
 * @see docs/_context/PETOTA_BUSINESS_IMPLEMENTATION_PLAN.md §Phase E FE rewire
 */

import { APP_ENV } from '@/shared/config/env';

import * as mockApi from './dashboard-api-mock';
import * as httpApi from './dashboard-api-http';

import type { DashboardSnapshot, GetDashboardSnapshotParams } from './types';

const useLive = APP_ENV.bizApiMode === 'live';

export async function getDashboardSnapshot(
  params: GetDashboardSnapshotParams,
): Promise<DashboardSnapshot> {
  return useLive
    ? httpApi.getDashboardSnapshot(params)
    : mockApi.getDashboardSnapshot(params);
}

export async function dismissDashboardInsight(insightId: string): Promise<void> {
  return useLive
    ? httpApi.dismissDashboardInsight(insightId)
    : mockApi.dismissDashboardInsight(insightId);
}

/**
 * Introspection helper — exposes the currently active mode so debug panels
 * and QA tooling can surface the dispatcher choice without re-reading env.
 */
export function getDashboardApiMode(): 'mock' | 'live' {
  return useLive ? 'live' : 'mock';
}
